import React, { useState, useEffect } from 'react';
import { ArrowTrendingUpIcon, ArrowTrendingDownIcon, MinusIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import MarketSentimentChartModal from './MarketSentimentChartModal';

interface FearGreedData {
  value: number;
  classification: string;
  createdAt: string;
}

interface NewsSentimentData {
  score: number;
  classification: string;
  articles: number;
  confidence: number;
}

interface MarketSentimentWidgetProps {
  className?: string;
  fearGreedData?: any;
  newsSentimentData?: any;
}

const MarketSentimentWidget: React.FC<MarketSentimentWidgetProps> = ({
  className = '',
  fearGreedData: propFearGreedData = null,
  newsSentimentData: propNewsSentimentData = null
}) => {
  const [fearGreed, setFearGreed] = useState<FearGreedData | null>(null);
  const [newsSentiment, setNewsSentiment] = useState<NewsSentimentData | null>(null);
  const [previousFearGreed, setPreviousFearGreed] = useState<FearGreedData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showChartModal, setShowChartModal] = useState(false);

  useEffect(() => {
    if (propFearGreedData || propNewsSentimentData) {
      processDataFromParent(propFearGreedData, propNewsSentimentData);
    } else {
      fetchData();
      const interval = setInterval(fetchData, 10 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [propFearGreedData, propNewsSentimentData]);

  const processDataFromParent = (fearGreedData: any, sentimentData: any) => {
    try {
      setLoading(true);
      setError(null);

      // Process Fear & Greed data
      if (fearGreedData?.current) {
        setFearGreed({
          value: fearGreedData.current.value,
          classification: fearGreedData.current.classification,
          createdAt: fearGreedData.current.createdAt
        });
      }

      if (fearGreedData?.historical && fearGreedData.historical.length > 0) {
        const previousData = fearGreedData.historical[0];
        setPreviousFearGreed({
          value: previousData.value,
          classification: previousData.classification,
          createdAt: previousData.createdAt
        });
      }

      // Process News Sentiment data
      if (sentimentData?.averageSentiment) {
        setNewsSentiment({
          score: sentimentData.averageSentiment.score,
          classification: sentimentData.averageSentiment.classification,
          articles: sentimentData.sentiment?.length || 0,
          confidence: sentimentData.sentiment?.length > 0 
            ? sentimentData.sentiment.reduce((sum: number, item: any) => sum + item.confidence, 0) / sentimentData.sentiment.length 
            : 0
        });
      } else if (sentimentData?.sentiment && sentimentData.sentiment.length > 0) {
        const avgScore = sentimentData.sentiment.reduce((sum: number, item: any) => sum + item.score, 0) / sentimentData.sentiment.length;
        const avgConfidence = sentimentData.sentiment.reduce((sum: number, item: any) => sum + item.confidence, 0) / sentimentData.sentiment.length;
        
        setNewsSentiment({
          score: avgScore,
          classification: getNewsClassification(avgScore),
          articles: sentimentData.sentiment.length,
          confidence: avgConfidence
        });
      }
    } catch (err) {
      console.error('Error processing Market Sentiment data from parent:', err);
      setError('Failed to process data');
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      setError(null);
      
      const { apiClient } = await import('@/services/api');
      const [fearGreedRes, sentimentRes] = await Promise.allSettled([
        apiClient.get<any>('/market/fear-greed', { limit: 2 }),
        apiClient.get<any>('/market/news-sentiment', { hours: 24 })
      ]);

      // Process Fear & Greed
      if (fearGreedRes.status === 'fulfilled') {
        const fearGreedData = (fearGreedRes.value as any).fearGreedIndex || (fearGreedRes.value as any).data?.fearGreedIndex;
        
        if (fearGreedData?.current) {
          setFearGreed({
            value: fearGreedData.current.value,
            classification: fearGreedData.current.classification,
            createdAt: fearGreedData.current.createdAt
          });
        }

        if (fearGreedData?.historical && fearGreedData.historical.length > 0) {
          setPreviousFearGreed({
            value: fearGreedData.historical[0].value,
            classification: fearGreedData.historical[0].classification,
            createdAt: fearGreedData.historical[0].createdAt
          });
        }
      }

      // Process News Sentiment
      if (sentimentRes.status === 'fulfilled') {
        const sentimentData = sentimentRes.value.data;
        
        if (sentimentData?.averageSentiment) {
          setNewsSentiment({
            score: sentimentData.averageSentiment.score,
            classification: sentimentData.averageSentiment.classification,
            articles: sentimentData.sentiment?.length || 0,
            confidence: sentimentData.sentiment?.length > 0 
              ? sentimentData.sentiment.reduce((sum: number, item: any) => sum + item.confidence, 0) / sentimentData.sentiment.length 
              : 0
          });
        }
      }

    } catch (err) {
      console.error('Error fetching Market Sentiment data:', err);
      setError('Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const getNewsClassification = (score: number): string => {
    if (score >= 70) return 'Very Bullish';
    if (score >= 60) return 'Bullish';
    if (score >= 40) return 'Neutral';
    if (score >= 30) return 'Bearish';
    return 'Very Bearish';
  };

  const getSentimentColor = (value: number, isNews: boolean = false) => {
    if (isNews) {
      // News sentiment: 0-100 scale
      if (value >= 70) return 'text-success-custom bg-success-subtle border-success-subtle';
      if (value >= 60) return 'text-success-custom bg-success-subtle border-success-subtle';
      if (value >= 40) return 'text-warning-custom bg-warning-subtle border-warning-subtle';
      if (value >= 30) return 'text-warning-custom bg-warning-subtle border-warning-subtle';
      return 'text-error-custom bg-error-subtle border-error-subtle';
    } else {
      // Fear & Greed: 0-100 scale
      if (value <= 24) return 'text-error-custom bg-error-subtle border-error-subtle';
      if (value <= 49) return 'text-warning-custom bg-warning-subtle border-warning-subtle';
      if (value === 50) return 'text-warning-custom bg-warning-subtle border-warning-subtle';
      if (value <= 75) return 'text-success-custom bg-success-subtle border-success-subtle';
      return 'text-info-custom bg-info-subtle border-info-subtle';
    }
  };

  const getTrendIcon = () => {
    if (!fearGreed || !previousFearGreed) return null;
    
    const change = fearGreed.value - previousFearGreed.value;
    if (Math.abs(change) < 1) {
      return <MinusIcon className="h-4 w-4 text-tertiary-custom" />;
    }
    
    return change > 0 
      ? <ArrowTrendingUpIcon className="h-4 w-4 text-green-500" />
      : <ArrowTrendingDownIcon className="h-4 w-4 text-red-500" />;
  };

  const getTrendText = () => {
    if (!fearGreed || !previousFearGreed) return '';
    
    const change = fearGreed.value - previousFearGreed.value;
    if (Math.abs(change) < 1) return 'No change';
    
    return change > 0 ? `+${change.toFixed(0)}` : `${change.toFixed(0)}`;
  };

  if (loading) {
    return (
      <div className={`card rounded-lg shadow p-4 ${className}`}>
        <div className="animate-pulse">
          <div className="h-4 bg-secondary-custom rounded mb-2"></div>
          <div className="h-8 bg-secondary-custom rounded mb-2"></div>
          <div className="h-3 bg-secondary-custom rounded w-2/3"></div>
        </div>
      </div>
    );
  }

  if (error || (!fearGreed && !newsSentiment)) {
    return (
      <div className={`card rounded-lg shadow p-4 ${className}`}>
        <div className="text-error-custom text-body">
          <p className="font-medium">Market Sentiment</p>
          <p>{error || 'No data'}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={`card rounded-lg shadow ${className}`}>
        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-body font-medium text-primary-custom">
              Market Sentiment
            </h3>
            <button
              onClick={() => setShowChartModal(true)}
              className="p-2 hover:bg-secondary-custom rounded-lg transition-colors"
              title="View historical trends"
            >
              <ChartBarIcon className="h-5 w-5 text-primary-custom" />
            </button>
          </div>

          {/* Side-by-side layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Fear & Greed Index */}
            {fearGreed && (
              <div className="pr-4 border-r border-primary-custom">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-caption text-secondary-custom font-medium">
                    Fear & Greed Index
                  </div>
                  {getTrendIcon()}
                </div>

                <div className="flex items-center justify-between mb-3">
                  <div 
                    className={`inline-flex items-center px-3 py-1 rounded-full text-body font-medium border ${getSentimentColor(fearGreed.value, false)}`}
                  >
                    {fearGreed.value}
                  </div>
                  {previousFearGreed && (
                    <div className="text-right">
                      <div className="text-caption text-secondary-custom">24h change</div>
                      <div className="text-body font-medium text-primary-custom">
                        {getTrendText()}
                      </div>
                    </div>
                  )}
                </div>

                <div className="mb-2">
                  <div className="text-body font-medium text-primary-custom mb-1">
                    {fearGreed.classification}
                  </div>
                  
                  {/* Progress bar */}
                  <div className="w-full bg-secondary-custom rounded-full h-2">
                    <div 
                      className="h-2 rounded-full transition-all duration-500"
                      style={{
                        width: `${fearGreed.value}%`,
                        backgroundColor: getSentimentColor(fearGreed.value, false).includes('error') ? '#dc2626' :
                                      getSentimentColor(fearGreed.value, false).includes('warning') ? '#ea580c' :
                                      getSentimentColor(fearGreed.value, false).includes('success') ? '#16a34a' : '#2563eb'
                      }}
                    ></div>
                  </div>
                  
                  <div className="flex justify-between text-caption text-secondary-custom mt-1">
                    <span>Extreme Fear</span>
                    <span>Extreme Greed</span>
                  </div>
                </div>

                <div className="text-caption text-tertiary-custom">
                  Updated {new Date(fearGreed.createdAt).toLocaleDateString()} at {new Date(fearGreed.createdAt).toLocaleTimeString()}
                </div>
              </div>
            )}

            {/* News Sentiment */}
            {newsSentiment && (
              <div className="pl-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-caption text-secondary-custom font-medium">
                    News Sentiment
                  </div>
                  {newsSentiment.score >= 50 ? (
                    <ArrowTrendingUpIcon className="h-4 w-4 text-green-500" />
                  ) : (
                    <ArrowTrendingDownIcon className="h-4 w-4 text-red-500" />
                  )}
                </div>

                <div className="flex items-center justify-between mb-3">
                  <div 
                    className={`inline-flex items-center px-3 py-1 rounded-full text-body font-medium border ${getSentimentColor(newsSentiment.score, true)}`}
                  >
                    {Math.round(newsSentiment.score)}
                  </div>
                  <div className="text-right">
                    <div className="text-caption text-secondary-custom">{newsSentiment.articles} sources</div>
                    <div className="text-body font-medium text-primary-custom">
                      {(newsSentiment.confidence * 100).toFixed(0)}% confidence
                    </div>
                  </div>
                </div>

                <div className="mb-2">
                  <div className="text-body font-medium text-primary-custom mb-1">
                    {newsSentiment.classification}
                  </div>
                  
                  {/* Progress bar */}
                  <div className="w-full bg-secondary-custom rounded-full h-2">
                    <div 
                      className="h-2 rounded-full transition-all duration-500"
                      style={{
                        width: `${newsSentiment.score}%`,
                        backgroundColor: getSentimentColor(newsSentiment.score, true).includes('error') ? '#dc2626' :
                                      getSentimentColor(newsSentiment.score, true).includes('warning') ? '#ea580c' :
                                      getSentimentColor(newsSentiment.score, true).includes('success') ? '#16a34a' : '#2563eb'
                      }}
                    ></div>
                  </div>
                  
                  <div className="flex justify-between text-caption text-secondary-custom mt-1">
                    <span>Very Bearish</span>
                    <span>Very Bullish</span>
                  </div>
                </div>

                <div className="text-caption text-tertiary-custom">
                  Based on last 24 hours
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Chart Modal */}
      {showChartModal && (
        <MarketSentimentChartModal
          isOpen={showChartModal}
          onClose={() => setShowChartModal(false)}
        />
      )}
    </>
  );
};

export default MarketSentimentWidget;

